/**
 * Admin-Panel P1 (docs/KONZEPT-ADMIN-PANEL.md § "P1 — Lesen"): Mandantenliste, rein lesend.
 * Backend: `GET /api/admin/tenants` (apps/api/src/routes/admin.ts) — Uebersicht ALLER Mandanten
 * (Slug, Name, erstellt am, Mitgliederzahl), 404 fuer Nicht-Admin/falschen Host (kein
 * Existenz-Orakel, wie `whoami` in `pages/admin/index.tsx`).
 *
 * Navigation OHNE Router (docs/LOOP-ADMIN-PANEL.md-Auftrag P1, Muster aus `App.tsx`
 * `istAdminHost`-Zweig): ein Klick auf eine Zeile reicht die Mandanten-ID per Callback an die
 * Admin-Shell (`pages/admin/index.tsx`) hoch, die per einfachem React-State zwischen dieser Liste
 * und `TenantDetail` umschaltet — kein neues `<Route path="...">`-Element, damit die
 * E2E-Abdeckungs-Ratsche (`apps/web/test/e2e-abdeckung.test.ts`) nicht anschlaegt.
 *
 * P2 Admin-Bereich V2 (docs/KONZEPT-ADMIN-BEREICH.md § "Lifecycle-UI", docs/LOOP-ADMIN-BEREICH.md
 * P2 siebtes Kriterium): `status`-Spalte via `StatusPill`/`tenantStatusMeta` (`ui/format.ts`) —
 * dasselbe Feld, das `GET /api/admin/tenants` seit Migration 375 in JEDER Zeile mitliefert
 * (`routes/admin.ts`, Kommentar dort: "noch OHNE Durchsetzungswirkung"). `onNeu` oeffnet den
 * neuen Onboarding-Assistenten (`/mandanten/neu`, `mandant-neu.tsx`) — Klick statt Link, aus
 * demselben Grund wie `KpiCard.onClick` (`ui/components.tsx`): der Mini-Router
 * (`hooks/useAdminNavigation.ts`) lauscht auf `popstate`, das ein echter `<a>`/`Link`-Klick nicht
 * ausloest.
 */
import { useApi } from "../../hooks/useApi";
import { formatDate, tenantStatusMeta } from "../../ui/format";
import { EmptyState, ErrorNote, Icon, LoadingRows, StatusPill } from "../../ui/components";

export interface AdminTenantRow {
  id: string;
  slug: string;
  name: string;
  createdAt: string;
  memberCount: number;
  /** Optional statt Pflichtfeld: `tenantStatusMeta(undefined)` liefert `undefined`, `StatusPill`
   *  zeigt dann nichts — robust auch ohne dieses Feld, statt bei fehlendem Wert abzustuerzen. */
  status?: string;
}

export function TenantsListe({
  onSelectTenant,
  onNeu,
}: {
  onSelectTenant: (tenantId: string) => void;
  onNeu: () => void;
}) {
  const { data, loading, error, reload } = useApi<AdminTenantRow[]>("/api/admin/tenants");
  const tenants = data ?? [];

  return (
    <div className="card mt-6 overflow-hidden">
      <div className="flex items-center justify-between gap-3 border-b border-outline-variant/30 px-4 py-3 dark:border-outline/30">
        <h2 className="font-body-md text-body-md font-semibold text-on-background dark:text-inverse-on-surface">Mandanten</h2>
        <button type="button" className="btn-primary !py-2" onClick={onNeu}>
          <Icon name="add" className="text-[16px]" /> Neuer Mandant
        </button>
      </div>
      {error && <ErrorNote message={error} onRetry={reload} />}
      {loading ? (
        <LoadingRows />
      ) : tenants.length === 0 ? (
        <EmptyState
          icon="apartment"
          title="Keine Mandanten gefunden"
          cta={{ label: "Neuer Mandant", onClick: onNeu, icon: "add" }}
        />
      ) : (
        <div className="overflow-x-auto">
          <table className="handy-karten w-full min-w-[560px] border-collapse text-left">
            <thead>
              <tr>
                <th className="table-header">Slug</th>
                <th className="table-header">Name</th>
                <th className="table-header">Status</th>
                <th className="table-header">Erstellt am</th>
                <th className="table-header text-right">Mitglieder</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-outline-variant/30 dark:divide-outline/30">
              {tenants.map((t) => (
                <tr
                  key={t.id}
                  className="cursor-pointer transition-colors hover:bg-surface-container-high/20 dark:hover:bg-surface-variant/20"
                  onClick={() => onSelectTenant(t.id)}
                >
                  <td className="table-cell font-mono text-body-sm text-on-background dark:text-inverse-on-surface">{t.slug}</td>
                  <td className="table-cell font-body-md text-body-md font-semibold text-on-background dark:text-inverse-on-surface">
                    {t.name}
                  </td>
                  <td className="table-cell">
                    <StatusPill meta={tenantStatusMeta(t.status)} testId={`tenant-status-${t.id}`} />
                  </td>
                  <td className="table-cell font-body-sm text-body-sm text-on-surface-variant dark:text-secondary-fixed-dim">
                    {formatDate(t.createdAt)}
                  </td>
                  <td className="table-cell text-right font-body-md text-body-md text-on-surface-variant dark:text-secondary-fixed-dim">
                    {t.memberCount}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
